import { useEffect, useState } from "react";
import {
  Car,
  Bike,
  Smartphone,
  Building2,
  Home,
  Wrench,
  Settings,
  Heart,
  Users,
  Sofa,
  Laptop,
  User,
  Briefcase,
  Shirt,
  Shield,
  Coins,
  Hammer,
  Scale,
  Tag,
  Gift,
  Compass,
  Droplet,
  HelpCircle,
  Truck,
  ChevronRight
} from "lucide-react";
import { useMarketplace, type Category } from "./marketplace-context";
import { listingsAPI } from "@/lib/api/listings";

const CATEGORIES = [
  { name: "Cars", apiKey: "CARS", icon: Car, tint: "bg-primary-soft text-primary" },
  { name: "Bikes", apiKey: "BIKES", icon: Bike, tint: "bg-accent-soft text-accent" },
  { name: "Mobiles", apiKey: "MOBILES", icon: Smartphone, tint: "bg-primary-soft text-primary" },
  { name: "Real Estate", apiKey: "REAL_ESTATE", icon: Building2, tint: "bg-accent-soft text-accent" },
  { name: "House Rents / Room Mates", apiKey: "RENT_ROOMMATES", icon: Home, tint: "bg-primary-soft text-primary" },
  { name: "Bus / Trucks / Autos / Other Vehicles", apiKey: "BUS_TRUCKS_AUTOS_VEHICLES", icon: Truck, tint: "bg-accent-soft text-accent" },
  { name: "Automobile Accessories", apiKey: "AUTOMOBILE_ACCESSORIES", icon: Settings, tint: "bg-primary-soft text-primary" },
  { name: "Pets & Hobbies", apiKey: "PETS_AND_HOBBIES", icon: Heart, tint: "bg-accent-soft text-accent" },
  { name: "Community & Events", apiKey: "COMMUNITY_AND_EVENTS", icon: Users, tint: "bg-primary-soft text-primary" },
  { name: "Furniture / Interiors", apiKey: "FURNITURE_INTERIORS", icon: Sofa, tint: "bg-accent-soft text-accent" },
  { name: "Electronics & Appliances", apiKey: "ELECTRONICS_AND_APPLIANCES", icon: Laptop, tint: "bg-primary-soft text-primary" },
  { name: "Personal & Community", apiKey: "PERSONAL_AND_COMMUNITY", icon: User, tint: "bg-accent-soft text-accent" },
  { name: "Jobs & Services", apiKey: "JOBS_AND_SERVICES", icon: Briefcase, tint: "bg-primary-soft text-primary" },
  { name: "Fashion & Lifestyle", apiKey: "FASHION_AND_LIFESTYLE", icon: Shirt, tint: "bg-accent-soft text-accent" },
  { name: "Safety / Protection", apiKey: "SAFETY_PROTECTION", icon: Shield, tint: "bg-primary-soft text-primary" },
  { name: "Business / Financial", apiKey: "BUSINESS_FINANCIAL", icon: Coins, tint: "bg-accent-soft text-accent" },
  { name: "Professional Services / Trades", apiKey: "PROFESSIONAL_SERVICES", icon: Hammer, tint: "bg-primary-soft text-primary" },
  { name: "Insurance / Legal", apiKey: "INSURANCE_LEGAL", icon: Scale, tint: "bg-accent-soft text-accent" },
  { name: "Maintenance & Repair", apiKey: "MAINTENANCE_AND_REPAIR", icon: Wrench, tint: "bg-primary-soft text-primary" },
  { name: "Personal Services", apiKey: "PERSONAL_SERVICES", icon: User, tint: "bg-accent-soft text-accent" },
  { name: "Sales / Auctions / Discounts", apiKey: "SALES_AUCTIONS", icon: Tag, tint: "bg-primary-soft text-primary" },
  { name: "Gifts / Special Occasions", apiKey: "GIFTS_SPECIAL_OCCASIONS", icon: Gift, tint: "bg-accent-soft text-accent" },
  { name: "Travel / Entertainment", apiKey: "TRAVEL_ENTERTAINMENT", icon: Compass, tint: "bg-primary-soft text-primary" },
  { name: "Water Filtration / Purification", apiKey: "WATER_FILTRATION", icon: Droplet, tint: "bg-accent-soft text-accent" },
  { name: "Security Guards", apiKey: "SECURITY_GUARDS", icon: Shield, tint: "bg-primary-soft text-primary" },
  { name: "Other Services", apiKey: "OTHER_SERVICES", icon: HelpCircle, tint: "bg-accent-soft text-accent" },
];

function formatCount(n: number): string {
  if (n === 0) return "0 ads";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M+ ads`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K+ ads`;
  return `${n} ads`;
}

export function Categories() {
  const { setCategory } = useMarketplace();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    listingsAPI
      .getCategoryCounts()
      .then((data) => setCounts(data))
      .catch(() => setCounts({}))
      .finally(() => setLoading(false));
  }, []);

  // Determine limit based on screen width to show exactly 2 rows
  const getLimit = () => {
    if (width >= 1024) return 11; // 6 cols -> 12 slots (11 categories + 1 "See all" card)
    if (width >= 768) return 7;   // 4 cols -> 8 slots (7 categories + 1 "See all" card)
    if (width >= 640) return 5;   // 3 cols -> 6 slots (5 categories + 1 "See all" card)
    return 3;                     // 2 cols -> 4 slots (3 categories + 1 "See all" card)
  };

  const limit = getLimit();
  const displayedCategories = isExpanded ? CATEGORIES : CATEGORIES.slice(0, limit);

  return (
    <section className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground md:text-3xl">Browse by category</h2>
          <p className="mt-1 text-sm text-muted-foreground">Find what you need across our categories.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {displayedCategories.map(({ name, apiKey, icon: Icon, tint }) => (
          <button
            key={name}
            type="button"
            onClick={() => {
              setCategory(name as Category);
              document.getElementById("listings-top")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="group flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[var(--shadow-card)] w-full"
          >
            <span className={`grid h-12 w-12 place-items-center rounded-xl ${tint} transition group-hover:scale-105`}>
              <Icon className="h-6 w-6" />
            </span>
            <div>
              <div className="font-heading text-sm font-semibold text-foreground line-clamp-2 leading-tight break-words">{name}</div>
              {loading ? (
                <div className="mt-1 h-3 w-14 animate-pulse rounded bg-muted" />
              ) : (
                <div className="text-xs text-muted-foreground">
                  {formatCount(counts[apiKey] ?? 0)}
                </div>
              )}
            </div>
          </button>
        ))}

        {!isExpanded ? (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="group flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[var(--shadow-card)] w-full"
          >
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary-soft text-primary transition group-hover:scale-105">
              <ChevronRight className="h-6 w-6" />
            </span>
            <div>
              <div className="font-heading text-sm font-semibold text-foreground">See all</div>
              <div className="text-xs text-muted-foreground">
                View all categories
              </div>
            </div>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="group flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[var(--shadow-card)] w-full"
          >
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent-soft text-accent transition group-hover:scale-105">
              <ChevronRight className="h-6 w-6 rotate-180" />
            </span>
            <div>
              <div className="font-heading text-sm font-semibold text-foreground">See less</div>
              <div className="text-xs text-muted-foreground">
                Collapse categories
              </div>
            </div>
          </button>
        )}
      </div>
    </section>
  );
}